import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, UserPlus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

import { getAuthErrorMessage, useNativeRegister } from '@/entities/auth';
import { Button, Form } from '@/shared/ui';

import { AuthShell } from './AuthShell';
import { AuthTextField } from './AuthTextField';

/** Минимальная длина пароля — требование бэка (`minLength: 8`). */
const MIN_PASSWORD = 8;

const schema = z
  .object({
    firstName: z
      .string()
      .trim()
      .min(2, 'Укажите имя')
      .max(50, 'Слишком длинное имя'),
    lastName: z
      .string()
      .trim()
      .min(2, 'Укажите фамилию')
      .max(50, 'Слишком длинная фамилия'),
    email: z
      .string()
      .min(1, 'Укажите почту')
      .email('Похоже, здесь опечатка в адресе'),
    password: z
      .string()
      .min(MIN_PASSWORD, `Не короче ${MIN_PASSWORD} символов`),
    passwordConfirm: z.string().min(1, 'Повторите пароль'),
  })
  .refine((values) => values.password === values.passwordConfirm, {
    path: ['passwordConfirm'],
    message: 'Пароли не совпадают',
  });

type RegisterForm = z.infer<typeof schema>;

export default function RegisterPage() {
  const navigate = useNavigate();

  const form = useForm<RegisterForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      passwordConfirm: '',
    },
  });

  const register = useNativeRegister();

  const onSubmit = form.handleSubmit((values) => {
    // Бэк на успешную регистрацию сразу отдаёт пару токенов — отдельного входа
    // после неё не требуется. Сессию проставляет onSuccess мутации.
    register.mutate(
      {
        email: values.email,
        password: values.password,
        first_name: values.firstName.trim(),
        last_name: values.lastName.trim(),
      },
      {
        onSuccess: () => {
          toast.success('Учётная запись создана');
          navigate('/', { replace: true });
        },
        onError: (error) => {
          const message = getAuthErrorMessage(
            error,
            'Не удалось зарегистрироваться'
          );
          toast.error(message);

          if (/почт/i.test(message)) {
            form.setError('email', { message });
          }
        },
      }
    );
  });

  const busy = register.isPending;

  return (
    <AuthShell
      title="Регистрация"
      subtitle="Создайте учётную запись DOIT"
      footer={
        <>
          Уже есть учётная запись?{' '}
          <Link
            to="/login"
            className="font-medium text-[var(--primary)] hover:underline"
          >
            Войти
          </Link>
        </>
      }
    >
      <Form {...form}>
        <form onSubmit={onSubmit} noValidate className="grid gap-4">
          <AuthTextField
            control={form.control}
            name="firstName"
            label="Имя"
            placeholder="Иван"
            autoComplete="given-name"
            disabled={busy}
          />

          <AuthTextField
            control={form.control}
            name="lastName"
            label="Фамилия"
            placeholder="Петров"
            autoComplete="family-name"
            disabled={busy}
          />

          <AuthTextField
            control={form.control}
            name="email"
            label="Почта"
            type="email"
            placeholder="name@company.kz"
            autoComplete="email"
            disabled={busy}
          />

          <AuthTextField
            control={form.control}
            name="password"
            label="Пароль"
            type="password"
            placeholder={`Минимум ${MIN_PASSWORD} символов`}
            autoComplete="new-password"
            disabled={busy}
          />

          <AuthTextField
            control={form.control}
            name="passwordConfirm"
            label="Повторите пароль"
            type="password"
            placeholder="Ещё раз"
            autoComplete="new-password"
            disabled={busy}
          />

          <Button
            type="submit"
            size="lg"
            disabled={busy}
            className="mt-2 w-full rounded-xl"
          >
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Создаём учётную запись…
              </>
            ) : (
              <>
                <UserPlus className="h-4 w-4" />
                Зарегистрироваться
              </>
            )}
          </Button>
        </form>
      </Form>
    </AuthShell>
  );
}
